import { ensureEditableCaretForInsert } from '../../../DocEditor/utils/atomicCaret';
import { separateNextUndoStep } from '../../../DocEditor/utils/undoSeparation';
import { insertTextAtCaret } from '../utils/caret';
import { scheduleClipboardWrite } from '../utils/largeValueGesture';
import { MAX_VALUE_DOM_CHARS, splicePlainText, valueCaretOffsets } from '../utils/nestedHtml';
import { placeCaretBeforeTail } from '../utils/placeFieldCaret';

/** Plain-text paste, and copy or cut of a fully selected heavy value. */
export function useNestedClipboard({
  elRef,
  readOnly,
  storedValue,
  readFull,
  ownsWholeValue,
  writeStored,
  clearStored,
  paint,
  handleInput,
}) {
  const handlePaste = (e) => {
    if (readOnly) return;
    // Always plain text in variable cells (no rich HTML / no video embed).
    e.preventDefault();
    const el = elRef.current;
    if (!el) return;
    // Park caret in a real text node before insert — flush against
    // a CE=false chip unwraps it (loses accent color → "white" value).
    ensureEditableCaretForInsert();
    // DocEditor owns Ctrl+Z; paste must open its own undo step
    separateNextUndoStep();
    const plain = (e.clipboardData?.getData('text/plain') ?? '').replace(/\r\n?/g, '\n');
    if (!plain) return;

    if (ownsWholeValue()) {
      writeStored(plain);
      return;
    }

    const marker = el.querySelector('[data-large-tail]');
    const stored = storedValue();
    const caret = valueCaretOffsets(el);
    const end = caret.coversTail ? stored.length : caret.end;
    const next = splicePlainText(stored, caret.start, end, plain);
    const sel = window.getSelection();
    const selInside = !!(sel?.anchorNode && el.contains(sel.anchorNode));

    // execCommand('insertText') builds a block per line and lays the whole
    // string out. A text node stays cheap; past the cap, only a preview is mounted.
    if (next.length > MAX_VALUE_DOM_CHARS || marker || !selInside) {
      paint(next);
      placeCaretBeforeTail(el);
      return;
    }

    insertTextAtCaret(plain);
    handleInput();
  };

  const handleCopy = (e) => {
    if (readOnly || !ownsWholeValue()) return;
    e.preventDefault();
    e.stopPropagation();
    scheduleClipboardWrite(readFull());
  };

  const handleCut = (e) => {
    if (readOnly) return;
    if (!ownsWholeValue()) {
      separateNextUndoStep();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    separateNextUndoStep();
    const text = readFull();
    clearStored();
    scheduleClipboardWrite(text);
  };

  return { handlePaste, handleCopy, handleCut };
}
